import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@Utils';

export interface PagerProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  /** 1-based. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  previousLabel: string;
  nextLabel: string;
  /** Names the nav landmark, e.g. "Store pagination". */
  ariaLabel: string;
}

const stepClasses =
  'inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-gray-500 transition-colors ' +
  'hover:bg-gray-50 hover:text-gray-700 disabled:pointer-events-none disabled:text-gray-300';

/** Compact `< 1 / 9 >` pager: two actions and a status label, so no selected item to announce. */
const Pager = ({
  page,
  pageCount,
  onPageChange,
  previousLabel,
  nextLabel,
  ariaLabel,
  className,
  ...props
}: PagerProps) => (
  <nav
    aria-label={ariaLabel}
    className={cn('inline-flex items-center gap-0.5 rounded-[9px] border border-gray-200 bg-white p-[3px]', className)}
    {...props}>
    <button
      type="button"
      aria-label={previousLabel}
      disabled={page <= 1}
      onClick={() => onPageChange(page - 1)}
      className={stepClasses}>
      <ChevronLeft size={14} />
    </button>
    {/* tabular-nums keeps the width steady so the chevrons don't shift on 9 -> 10 */}
    <span className="min-w-[48px] text-center text-xs font-semibold tabular-nums text-gray-500">
      {page} / {pageCount}
    </span>
    <button
      type="button"
      aria-label={nextLabel}
      disabled={page >= pageCount}
      onClick={() => onPageChange(page + 1)}
      className={stepClasses}>
      <ChevronRight size={14} />
    </button>
  </nav>
);

export { Pager };
